import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import { useCustomerDeleteHandlers } from '@/sections/customerManagement/hooks/actions';
import {
  ActionSource,
  CustomerData,
} from '@/sections/customerManagement/types';

interface DeleteCustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: CustomerData[];
  source?: ActionSource;
  onSuccess?: () => void;
}

/**
 * Delete Customers Modal
 *
 * Confirmation modal for customer deletion operations.
 * Supports both single customer deletion (from drawer) and bulk deletion (from table).
 */
export const DeleteCustomersModal: React.FC<DeleteCustomersModalProps> = ({
  isOpen,
  onClose,
  customers,
  source: _source = 'drawer',
  onSuccess,
}) => {
  const { handleDeleteCustomers, isPending } = useCustomerDeleteHandlers();
  const [confirmText, setConfirmText] = useState('');

  // Determine if this is a bulk operation
  const isBulk = customers.length > 1;
  const customer = customers[0];

  // Require typing "DELETE" for confirmation
  const isConfirmed = confirmText.toUpperCase() === 'DELETE';

  const handleDelete = async () => {
    if (!isConfirmed) {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      const result = await handleDeleteCustomers(customers);

      // Show appropriate success message
      if (result.failed === 0) {
        toast.success(
          isBulk
            ? `Marked ${result.succeeded} customers for deletion`
            : `Account for ${customer.displayName} has been marked for deletion`
        );
      } else if (result.succeeded > 0) {
        toast.warning(
          `Marked ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''} for deletion, ` +
            `but failed for ${result.failed} customer${result.failed > 1 ? 's' : ''}`
        );
      }

      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error('Failed to delete customers');
      console.error('[DeleteCustomersModal] Delete failed:', error);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={isBulk ? 'Delete Customers' : 'Delete Customer'}
      subtitle={
        isBulk
          ? `Confirm deletion of ${customers.length} customers`
          : `Confirm deletion of ${customer?.displayName}`
      }
      size="lg"
      footer={{
        text: isPending ? 'Deleting...' : 'Delete',
        onClick: handleDelete,
        disabled: !isConfirmed || isPending,
      }}
    >
      <div className="flex flex-col gap-4">
        {/* Warning banner */}
        <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <ExclamationTriangleIcon className="size-5 flex-shrink-0 text-red-600 dark:text-red-400" />
          <div>
            <p className="mb-1 text-sm font-medium text-red-800 dark:text-red-200">
              Warning: This action cannot be undone
            </p>
            <p className="text-sm text-red-700 dark:text-red-300">
              {isBulk
                ? `Deleting ${customers.length} customers will permanently remove their accounts and all associated data.`
                : `Deleting ${customer?.displayName}'s account will permanently remove all associated data.`}
            </p>
          </div>
        </div>

        {/* Customer list for bulk operations */}
        {isBulk && (
          <div className="bg-chariot-background-secondary max-h-48 overflow-y-auto rounded-md p-3">
            <p className="text-chariot-text-secondary mb-2 text-xs font-medium uppercase">
              Customers to be deleted:
            </p>
            <ul className="space-y-1">
              {customers.map((c, idx) => (
                <li key={c.email} className="text-chariot-text-primary text-sm">
                  {idx + 1}. {c.displayName} ({c.email})
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Confirmation input */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="confirm-delete"
            className="text-chariot-text-primary text-sm font-medium"
          >
            Type <span className="font-mono text-red-600">DELETE</span> to
            confirm:
          </label>
          <input
            id="confirm-delete"
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="DELETE"
            disabled={isPending}
            className="border-chariot-stroke focus:border-chariot-brand-primary focus:ring-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-hidden"
          />
        </div>
      </div>
    </Modal>
  );
};
